import type { Resource } from '../../../../shared/models/Resource';

export type EditorContentSource =
  | 'canonical'
  | 'legacy'
  | 'empty'
  | 'invalid-canonical-fallback';

export interface ResourceEditorState {
  source: EditorContentSource;
  contentHtml: string;
  canonicalDocument?: unknown;
}

const DRAFT_DOCUMENT_ID =
  '00000000-0000-4000-8000-000000000000';

const isObject = (
  value: unknown
): value is Record<string, unknown> => {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value)
  );
};

const deepClone = <T,>(value: T): T => {
  return JSON.parse(JSON.stringify(value));
};

const extractPlainTextFromCanonical = (
  value: unknown
): string => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        extractPlainTextFromCanonical(item)
      )
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  if (!isObject(value)) {
    return '';
  }

  if (
    value.type === 'text' &&
    typeof value.text === 'string'
  ) {
    return value.text;
  }

  if (Array.isArray(value.content)) {
    return value.content
      .map((item) =>
        extractPlainTextFromCanonical(item)
      )
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return '';
};

const stripHtmlToText = (value: string): string => {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const isCanonicalHydrationSafe = (
  resource?: Partial<Resource> | null
): boolean => {
  try {
    if (!resource || !isObject(resource)) {
      return false;
    }

    const contentDocument =
      resource.contentDocument;

    if (!isObject(contentDocument)) {
      return false;
    }

    if (contentDocument.type !== 'doc') {
      return false;
    }

    if (
      !Number.isInteger(contentDocument.version)
    ) {
      return false;
    }

    if (!isObject(contentDocument.attrs)) {
      return false;
    }

    if (
      typeof contentDocument.attrs
        .documentId !== 'string' ||
      !contentDocument.attrs.documentId.trim()
    ) {
      return false;
    }

    if (!Array.isArray(contentDocument.content)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

export const fromResourceToEditorState = (
  resource?: Partial<Resource> | null
): ResourceEditorState => {
  try {
    const hasSafeCanonical =
      isCanonicalHydrationSafe(resource);

    const legacyContent =
      typeof resource?.content === 'string'
        ? resource.content
        : '';

    if (hasSafeCanonical) {
      const canonicalDocument =
        resource?.contentDocument;
      const canonicalHtml =
        typeof resource?.contentHtmlSanitized ===
        'string'
          ? resource.contentHtmlSanitized.trim()
          : '';

      return {
        source:
          canonicalHtml.length > 0
            ? 'canonical'
            : 'invalid-canonical-fallback',
        contentHtml:
          canonicalHtml.length > 0
            ? canonicalHtml
            : legacyContent,
        canonicalDocument: deepClone(
          canonicalDocument
        )
      };
    }

    if (legacyContent.trim()) {
      return {
        source:
          resource?.contentDocument
            ? 'invalid-canonical-fallback'
            : 'legacy',
        contentHtml: legacyContent
      };
    }

    return {
      source: 'empty',
      contentHtml: ''
    };
  } catch {
    return {
      source: 'legacy',
      contentHtml:
        typeof resource?.content === 'string'
          ? resource.content
          : ''
    };
  }
};

export const fromEditorStateToCanonical = (
  editorState: ResourceEditorState
): Record<string, unknown> => {
  const plainText = stripHtmlToText(
    editorState.contentHtml || ''
  );

  return {
    type: 'doc',
    version: 1,
    attrs: {
      documentId: DRAFT_DOCUMENT_ID,
      direction: 'auto',
      language: 'en'
    },
    content: plainText
      ? [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: plainText
              }
            ]
          }
        ]
      : []
  };
};

export const fromEditorStateToLegacyHtml = (
  editorState: ResourceEditorState,
  options?: {
    allowCanonicalFallback?: boolean;
  }
): string => {
  const allowCanonicalFallback =
    options?.allowCanonicalFallback !== false;

  const currentHtml =
    typeof editorState.contentHtml === 'string'
      ? editorState.contentHtml.trim()
      : '';

  if (currentHtml) {
    return currentHtml;
  }

  if (!allowCanonicalFallback) {
    return '';
  }

  const plainText =
    extractPlainTextFromCanonical(
      editorState.canonicalDocument
    );

  if (!plainText) {
    return '';
  }

  return `<p>${escapeHtml(plainText)}</p>`;
};
